import React, { useState } from 'react';
import styled from 'styled-components/macro';
import { Body, Button, Dropdown, Flex, Heading, Menu } from '@cognite/cogs.js';
import { ConfirmationDialog } from 'components/ConfirmationDialog/ConfirmationDialog';
import { ConsistencySchemaFilterView } from '../types';

enum ViewOption {
  Card = 'card',
  List = 'list',
}

type SchemaFilterListProps = {
  schemaFilters: ConsistencySchemaFilterView[];
  viewOption: ViewOption;
  onSchemaFilterClick?: (schemaFilter: ConsistencySchemaFilterView) => void;
  onEditSchemaFilter?: (id: string) => void;
  onDeleteSchemaFilter?: (id: string) => void;
};

export { ViewOption };

type ItemProps = {
  schemaFilter: ConsistencySchemaFilterView;
  onSchemaFilterClick?: (schemaFilter: ConsistencySchemaFilterView) => void;
  onEditSchemaFilter?: (id: string) => void;
  onDeleteSchemaFilter?: (id: string) => void;
};

type RowProps = {
  schemaFilter: ConsistencySchemaFilterView;
  onEditSchemaFilter?: (id: string) => void;
  onRequestDelete: () => void;
};

type ActionsProps = {
  onEdit: () => void;
  onDelete: () => void;
};

function SchemaFilterActions({ onEdit, onDelete }: ActionsProps) {
  return (
    <Dropdown
      content={
        <Menu>
          <Menu.Item
            onClick={onEdit}
            aria-label="Edit schema filter"
            icon="Edit"
            iconPlacement="left"
          >
            <span>Edit schema filter</span>
          </Menu.Item>
          <Menu.Item
            onClick={onDelete}
            aria-label="Delete schema filter"
            icon="Delete"
            iconPlacement="left"
            destructive
          >
            <span>Delete schema filter</span>
          </Menu.Item>
        </Menu>
      }
    >
      <Button
        icon="EllipsisVertical"
        type="ghost"
        size="small"
        aria-label="Schema filter options"
      />
    </Dropdown>
  );
}

function SchemaFilterCard({
  schemaFilter,
  onSchemaFilterClick,
  onEditSchemaFilter,
  onDeleteSchemaFilter,
}: ItemProps) {
  const [confirmingDelete, setConfirmingDelete] = useState(false);

  return (
    <>
      <StyledSchemaFilterCard
        onClick={() => onSchemaFilterClick?.(schemaFilter)}
      >
        <CardHeader>
          <Heading level={5}>{schemaFilter.name}</Heading>
          <CardMenuWrapper onClick={(e) => e.stopPropagation()}>
            <SchemaFilterActions
              onEdit={() => onEditSchemaFilter?.(schemaFilter.externalId)}
              onDelete={() => setConfirmingDelete(true)}
            />
          </CardMenuWrapper>
        </CardHeader>
        <Body size="small">
          <strong>Schema:</strong> {schemaFilter.schemaCollectionName}
        </Body>
        <Body size="small">
          <strong>Type:</strong> {schemaFilter.schemaType}
        </Body>
      </StyledSchemaFilterCard>

      {confirmingDelete && (
        <ConfirmationDialog
          title="Delete schema filter"
          message={`Are you sure you want to delete "${schemaFilter.name}"?`}
          textOk="Delete"
          buttonTypeOk="destructive"
          onOk={() => {
            onDeleteSchemaFilter?.(schemaFilter.externalId);
            setConfirmingDelete(false);
          }}
          onClose={() => setConfirmingDelete(false)}
        />
      )}
    </>
  );
}

function SchemaFilterRow({
  schemaFilter,
  onEditSchemaFilter,
  onRequestDelete,
}: RowProps) {
  return (
    <tr>
      <td>
        <strong>{schemaFilter.name}</strong>
      </td>
      <td>{schemaFilter.schemaCollectionName}</td>
      <td>{schemaFilter.schemaType}</td>
      <td>
        <SchemaFilterActions
          onEdit={() => onEditSchemaFilter?.(schemaFilter.externalId)}
          onDelete={onRequestDelete}
        />
      </td>
    </tr>
  );
}

export const SchemaFilterList = ({
  schemaFilters,
  viewOption,
  onSchemaFilterClick,
  onEditSchemaFilter,
  onDeleteSchemaFilter,
}: SchemaFilterListProps) => {
  const [deletingSchemaFilter, setDeletingSchemaFilter] = useState<
    ConsistencySchemaFilterView | undefined
  >();

  if (schemaFilters.length === 0) {
    return (
      <StyledEmptyMessage size="small">
        No schema filters found. Create a schema filter to define filtered
        datasets for comparison.
      </StyledEmptyMessage>
    );
  }

  if (viewOption === ViewOption.Card) {
    return (
      <StyledSchemaFilterCards>
        {schemaFilters.map((schemaFilter) => (
          <SchemaFilterCard
            key={schemaFilter.externalId}
            schemaFilter={schemaFilter}
            onSchemaFilterClick={onSchemaFilterClick}
            onEditSchemaFilter={onEditSchemaFilter}
            onDeleteSchemaFilter={onDeleteSchemaFilter}
          />
        ))}
      </StyledSchemaFilterCards>
    );
  }

  return (
    <>
      <StyledTable>
        <thead>
          <tr>
            <th>Name</th>
            <th>Schema</th>
            <th>Type</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {schemaFilters.map((schemaFilter) => (
            <SchemaFilterRow
              key={schemaFilter.externalId}
              schemaFilter={schemaFilter}
              onEditSchemaFilter={onEditSchemaFilter}
              onRequestDelete={() => setDeletingSchemaFilter(schemaFilter)}
            />
          ))}
        </tbody>
      </StyledTable>

      {deletingSchemaFilter && (
        <ConfirmationDialog
          title="Delete schema filter"
          message={`Are you sure you want to delete "${deletingSchemaFilter.name}"?`}
          textOk="Delete"
          buttonTypeOk="destructive"
          onOk={() => {
            onDeleteSchemaFilter?.(deletingSchemaFilter.externalId);
            setDeletingSchemaFilter(undefined);
          }}
          onClose={() => setDeletingSchemaFilter(undefined)}
        />
      )}
    </>
  );
};

const StyledEmptyMessage = styled(Body)`
  color: var(--cogs-greyscale-grey6);
  font-style: italic;
  text-align: center;
  padding: 2rem;
`;

const StyledSchemaFilterCards = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(230px, 1fr));
  gap: 1rem;
  width: 100%;
`;

const StyledSchemaFilterCard = styled.div`
  background: var(--cogs-decorative--grayscale--white);
  border: 1px solid var(--cogs-bg-control--disabled);
  border-radius: 6px;
  padding: 1rem;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    border-color: var(--cogs-primary);
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  }

  .cogs-title-5 {
    margin-bottom: 0.5rem;
  }

  .cogs-body-4 {
    margin-bottom: 0.25rem;
  }
`;

const CardHeader = styled(Flex)`
  align-items: flex-start;
  justify-content: space-between;
  gap: 0.25rem;
  margin-bottom: 0.5rem;
`;

const CardMenuWrapper = styled(Flex)`
  flex-shrink: 0;
  margin-top: -4px;
  margin-right: -8px;
`;

const StyledTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  background: var(--cogs-decorative--grayscale--white);
  border-radius: 6px;
  overflow: hidden;
  border: 1px solid var(--cogs-bg-control--disabled);

  th,
  td {
    padding: 1rem;
    text-align: left;
    border-bottom: 1px solid var(--cogs-greyscale-grey3);
  }

  th {
    background: var(--cogs-greyscale-grey2);
    font-weight: 600;
    font-size: 14px;
  }

  tbody tr:hover {
    background: var(--cogs-greyscale-grey1);
  }

  tbody tr:last-child td {
    border-bottom: none;
  }
`;
